import type { CardFlavour } from '@components/Card2/types';
import { cardEditContext } from '@context/card-edit-context';
import { isEqual } from 'es-toolkit';
import { useContext, useEffect, useMemo, useReducer, useState } from 'react';

import { useCardAttributeLock } from './card/use-card-attribute-lock';
import type { CardProperty, Maybe, ValueProvider } from '.cache/__types__';

export enum UpdateNameFieldAction {
  TOGGLE_LOCK,
  UPDATE_FIRST_NAME,
  UPDATE_LAST_NAME,
  UPDATE_PREFIX,
  UPDATE_SUFFIX,
  UPDATE_MAIDEN_NAME,
  UPDATE_MIDDLE_NAME,
  UPDATE_PREFERRED_NAME,
  UPDATE_PRONOUNS,
  UPDATE_VALUE_PROVIDER,
  RESET_NAME_FIELDS,
}

type SetNameFieldAction = {
  type: UpdateNameFieldAction;
  value: string;
};

type ToggleLockAction = {
  type: UpdateNameFieldAction.TOGGLE_LOCK;
};

type SetNameFieldState = {
  type: 'SET';
  newState: CardNameFields;
};

type ResetNameFieldStateAction = {
  type: UpdateNameFieldAction.RESET_NAME_FIELDS;
};

export type Action =
  | SetNameFieldAction
  | SetNameFieldState
  | ToggleLockAction
  | ResetNameFieldStateAction;

function reducer(state: CardNameFields, action: Action): CardNameFields {
  switch (action.type) {
    case UpdateNameFieldAction.TOGGLE_LOCK:
      return { ...state, locked: !state.locked };
    case UpdateNameFieldAction.UPDATE_FIRST_NAME:
      return { ...state, firstName: action.value };
    case UpdateNameFieldAction.UPDATE_LAST_NAME:
      return { ...state, lastName: action.value };
    case UpdateNameFieldAction.UPDATE_PREFIX:
      return { ...state, honorificPrefix: action.value };
    case UpdateNameFieldAction.UPDATE_SUFFIX:
      return { ...state, honorificSuffix: action.value };
    case UpdateNameFieldAction.UPDATE_MAIDEN_NAME:
      return { ...state, maidenName: action.value };
    case UpdateNameFieldAction.UPDATE_MIDDLE_NAME:
      return { ...state, middleName: action.value };
    case UpdateNameFieldAction.UPDATE_PREFERRED_NAME:
      return { ...state, preferredName: action.value };
    case UpdateNameFieldAction.UPDATE_PRONOUNS:
      return { ...state, pronouns: action.value };
    case 'SET':
      return { ...action.newState };
    case UpdateNameFieldAction.UPDATE_VALUE_PROVIDER:
      return { ...state, valueProvider: action.value as ValueProvider }; // TODO fix this naughty casting

    /**
     * This action is used to reset the card name state when an admin assigns the field to a member to complete at a later date.
     * This is because if the admin fills in the name field, but then decides to let the employee modify the name, we must reset it back ready for them.
     */
    case UpdateNameFieldAction.RESET_NAME_FIELDS:
      return {
        valueProvider: state.valueProvider,
        locked: false,
        firstName: '',
        lastName: '',
        middleName: '',
        honorificPrefix: '',
        honorificSuffix: '',
        preferredName: '',
        maidenName: '',
        pronouns: '',
      };
    default:
      return state;
  }
}

type NameFields = {
  first_name: Maybe<CardProperty> | undefined;
  honorific_prefix: Maybe<CardProperty> | undefined;
  honorific_suffix: Maybe<CardProperty> | undefined;
  last_name: Maybe<CardProperty> | undefined;
  maiden_name: Maybe<CardProperty> | undefined;
  middle_name: Maybe<CardProperty> | undefined;
  preferred_name: Maybe<CardProperty> | undefined;
  pronouns: Maybe<CardProperty> | undefined;
};

export interface CardNameFields {
  locked: boolean;
  firstName: string;
  middleName: string;
  lastName: string;
  honorificPrefix: string;
  honorificSuffix: string;
  preferredName: string;
  maidenName: string;
  pronouns: string;
  valueProvider?: ValueProvider;
}

const extractNameFieldValues = (
  fields: NameFields,
  flavour: CardFlavour | undefined,
): CardNameFields => {
  const cardNameFields: CardNameFields = {
    locked: fields.first_name?.value_locked || false,
    firstName: fields?.first_name?.value || '',
    honorificPrefix: fields?.honorific_prefix?.value || '',
    honorificSuffix: fields?.honorific_suffix?.value || '',
    lastName: fields?.last_name?.value || '',
    maidenName: fields?.maiden_name?.value || '',
    middleName: fields?.middle_name?.value || '',
    preferredName: fields?.preferred_name?.value || '',
    pronouns: fields?.pronouns?.value || '',
    valueProvider: fields?.first_name?.value_provider || flavour === 'claim' ? 'member' : 'admin',
  };
  return cardNameFields;
};

type CardFieldState = {
  state: CardNameFields;
  fullyLocked: boolean;
  isStateModified: boolean;
  updateState: (value: Action) => void;
  onCancel: () => void;
};

export function useCardNameState(fields: NameFields, editMode: boolean): CardFieldState {
  const { flavour } = useContext(cardEditContext);

  const initialValueProvider: ValueProvider = flavour === 'claim' ? 'member' : 'admin';

  const [state, updateState] = useReducer(reducer, {
    locked: false,
    firstName: '',
    middleName: '',
    lastName: '',
    honorificPrefix: '',
    honorificSuffix: '',
    maidenName: '',
    preferredName: '',
    pronouns: '',
    valueProvider: initialValueProvider,
  });

  const { canUnlock } = useCardAttributeLock({
    flavour,
    valueLocked: state.locked,
    valueProvider: state.valueProvider,
    labelLocked: undefined,
    labelLockedByTemplate: undefined,
  });

  // Check that the state is equal to the default form values
  const isStateModified = useMemo(
    () => !isEqual(extractNameFieldValues(fields, flavour), state),
    [state, fields, flavour],
  );

  // Use primitive values as dependencies to avoid resetting state on object reference changes.
  // This is critical for Android Chrome where viewport resizes (when keyboard opens) can cause
  // parent components to re-render and pass new object references even when values haven't changed.
  // biome-ignore lint: eslint/react-hooks/exhaustive-deps see above
  useEffect(() => {
    const newState = extractNameFieldValues(fields, flavour);
    updateState({
      type: 'SET',
      newState,
    });
  }, [
    fields?.first_name?.value,
    fields?.first_name?.value_locked,
    fields?.first_name?.value_provider,
    fields?.last_name?.value,
    fields?.middle_name?.value,
    fields?.honorific_prefix?.value,
    fields?.honorific_suffix?.value,
    fields?.preferred_name?.value,
    fields?.maiden_name?.value,
    fields?.pronouns?.value,
    flavour,
  ]);

  const onCancel = () => {
    const newState = extractNameFieldValues(fields, flavour);
    updateState({ type: 'SET', newState });
  };

  return {
    state,
    fullyLocked: !canUnlock,
    isStateModified,
    updateState,
    onCancel,
  };
}
